import { CommonModule } from "@angular/common";
import {
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    output
} from "@angular/core";
import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { NgbDatepickerModule } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { IconsModule } from "../../../../icons/icons.module";
import { ApiError } from "../../../../models/types/api-error";
import { Employee } from "../../../../models/types/employee";
import { EmployeeService } from "../../../../service/employee/employee.service";
import { FormControlComponent } from "../../formcontrol/formcontrol.component";

@Component({
    selector: "app-new-employee-form",
    imports: [FormsModule, ReactiveFormsModule, CommonModule, FormControlComponent, NgbDatepickerModule, IconsModule],
    templateUrl: "./new-employee-form.component.html",
    styleUrl: "./new-employee-form.component.scss"
})

/**
 * Creates a self contained new employee form.
 * Outputs employee record on succesful submission
 * Outputs failure reasons on unsuccessful calls or form validation errors
 *
 * Requires branch-id and cu-id as inputs
 */
export class NewEmployeeFormComponent implements OnInit, OnDestroy {
    // branchId = input.required<number | null>();
    @Input({alias: "branch-id", required: true}) branchId: number | null = null;
    @Input({alias: "cu-id", required: true}) CreditUnionId: number | null = null;
    form$ = output<FormResults>();

    private employeeservice = inject(EmployeeService);
    private fb = inject(FormBuilder);
    private destroy$ = new Subject<void>();

    private _employee: Employee | null = null;
    employeeCreated: boolean = false;
    form!: ReturnType<typeof this.initializeForm>;

    constructor() {
        this.form = this.initializeForm();
    }
    ngOnInit(): void {}
    ngOnDestroy(): void {
        this.destroy$.unsubscribe();
    }

    convertToModel(form: FormGroup): Employee {
        return {
            id: form.get("id")?.value,
            firstName: form.get("firstName")?.value,
            lastName: form.get("lastName")?.value,
            displayName: form.get("displayName")?.value,
            email: form.get("email")?.value,
            primaryPhone: form.get("primaryPhone")?.value,
            affinityGroupId: this.CreditUnionId,
            branchId: this.branchId
        };
    }

    onEmployeeFormSubmit() {
        if (this.form!.invalid) return;

        //create a new employee
        if (!this.employeeCreated) {
            this.onEmployeeCreate();
        } else {
            this.onEmployeeUpdate();
        }
    }

    onEmployeeCreate() {
        let employee = this.convertToModel(this.form!);
        console.log(employee);

        this.employeeservice.saveEmployee(employee).subscribe((next) => {
            // this.outputValue.emit(next.result!);

            const formResults: FormResults = {
                errors: next.errors,
                values: next.result
            };

            this.form$?.emit(formResults);

            if (!next.errors) {
                this.employeeCreated = true;
                this._employee = next.result;
            }
        });
    }

    onEmployeeUpdate() {
        if (this.form!.invalid) return;

        let employee = this.convertToModel(this.form!);
        employee.id = this._employee?.id;

        this.employeeservice.updateEmployee(employee).subscribe((next) => {
            const formResults: FormResults = {
                errors: next.errors,
                values: next.result
            };

            this.form$?.emit(formResults);
        });
    }

    initializeForm() {
        return this.fb.nonNullable.group({
            id: new FormControl<number | null>(null),
            firstName: new FormControl<string | null>(null, {
                validators: [Validators.required, Validators.maxLength(50), Validators.pattern(/^[\p{L}\-']+$/u)]
            }),
            lastName: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(/^[\p{L}\-']+$/u)
            ]),
            email: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(50),
                Validators.email
            ]),
            primaryPhone: new FormControl<string | null>(null, [
                Validators.required,
                Validators.pattern(/^[2-9]\d{9}$/)
            ])
        });
    }
}
export type FormResults = {
    errors: Record<string, any> | ApiError[] | string | null;
    values: Employee | null;
};
// type FormControlToModel<TControl, TModel> = {
//     [K in keyof TModel]: TModel[K] extends infer TModel ? TModel : TControl extends {value: infer V} ? V : never;
// };
// type Model<TControl, TModel> = FormControlToModel<TControl, TModel>;
